// AudioOverlay — promoted from learner-app/components/audio
// to @flowgenix/shared-ui (Phase 2 / Step C-3.3).
//
// Renderer for the `audio_overlay` widget kind. Plays a coach-narration MP3
// for the current event with browser SpeechSynthesis as a fallback when the
// MP3 fails (autoplay block, missing TTS key, etc.).
//
// IMPORTANT — autoplay must NOT loop on parent re-renders. See the long
// header inside the component for the dep-array discipline. Behaviour is
// preserved bit-for-bit from the learner-app source; only the import path
// has changed.
//
// New in the promoted version:
//   • `mode` prop: 'docked' (default, position: fixed bottom-right — original
//     learner-app behaviour) | 'inline' (no position; consumer controls
//     placement). RN-Web consumers should pass mode="inline".
import React, { useRef, useState, useEffect, useCallback } from 'react';
import './AudioOverlay.css';

function useBrowserTTS() {
  const utteranceRef = useRef(null);
  const speakingRef = useRef(false);

  const speak = useCallback((text, onDone) => {
    if (typeof window === 'undefined' || !window.speechSynthesis || !text) return false;
    if (speakingRef.current) {
      window.speechSynthesis.cancel();
    }
    const utt = new SpeechSynthesisUtterance(text);
    utt.rate = 0.95;
    utt.pitch = 1.0;
    utt.onend = () => { speakingRef.current = false; if (onDone) onDone(); };
    utt.onerror = () => { speakingRef.current = false; if (onDone) onDone(); };
    utteranceRef.current = utt;
    speakingRef.current = true;
    window.speechSynthesis.speak(utt);
    return true;
  }, []);

  const stop = useCallback(() => {
    speakingRef.current = false;
    if (typeof window !== 'undefined') {
      window.speechSynthesis?.cancel();
    }
  }, []);

  const isSpeaking = useCallback(() => speakingRef.current, []);

  return { speak, stop, isSpeaking };
}

export default function AudioOverlay({
  audioUrl,
  fallbackText,
  autoPlay,
  onEnded,
  onPlay,
  playbackRate = 1.0,
  mode = 'docked',
}) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [usingSpeech, setUsingSpeech] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const prevUrlRef = useRef(null);
  const prevFallbackRef = useRef(null);
  const audioUrlRef = useRef(audioUrl);
  const fallbackTextRef = useRef(fallbackText);
  const onEndedRef = useRef(onEnded);
  const onPlayRef = useRef(onPlay);

  useEffect(() => { audioUrlRef.current = audioUrl; }, [audioUrl]);
  useEffect(() => { fallbackTextRef.current = fallbackText; }, [fallbackText]);
  useEffect(() => { onEndedRef.current = onEnded; }, [onEnded]);
  useEffect(() => { onPlayRef.current = onPlay; }, [onPlay]);

  const { speak, stop, isSpeaking } = useBrowserTTS();

  // Per-tab audio event log. Demo sweep verifier reads this to assert
  // exactly one audio element plays per timeline tick (regression
  // guard for the "two voices" bug — see fix5 commit message).
  const _logAudioEvent = (entry) => {
    if (typeof window === 'undefined') return;
    if (!Array.isArray(window.__AUDIO_EVENT_LOG__)) {
      window.__AUDIO_EVENT_LOG__ = [];
    }
    window.__AUDIO_EVENT_LOG__.push({ ts: Date.now(), ...entry });
  };

  const play = useCallback(() => {
    const url = audioUrlRef.current;
    const txt = fallbackTextRef.current;
    setBlocked(false);
    if (url && audioRef.current) {
      audioRef.current.play().then(() => {
        setPlaying(true);
        setUsingSpeech(false);
        _logAudioEvent({ kind: 'audio', url, source: 'overlay' });
        if (onPlayRef.current) onPlayRef.current();
      }).catch(() => {
        setBlocked(true);
      });
    } else if (txt && typeof window !== 'undefined' && window.speechSynthesis) {
      setUsingSpeech(true);
      setPlaying(true);
      _logAudioEvent({ kind: 'speech', text: txt, source: 'overlay' });
      if (onPlayRef.current) onPlayRef.current();
      speak(txt, () => { setPlaying(false); if (onEndedRef.current) onEndedRef.current(); });
    }
  }, [speak]);

  useEffect(() => {
    if (!autoPlay) return;
    if (!audioUrl && !fallbackText) return;

    const urlChanged = audioUrl !== prevUrlRef.current;
    const fallbackChanged = fallbackText !== prevFallbackRef.current;
    if (!urlChanged && !fallbackChanged) return;

    prevUrlRef.current = audioUrl;
    prevFallbackRef.current = fallbackText;

    if (isSpeaking()) {
      stop();
    }
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch (_) { /* element may not yet be ready */ }
    }
    setPlaying(false);

    const timer = setTimeout(() => play(), 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [audioUrl, fallbackText, autoPlay]);

  useEffect(() => {
    return () => { stop(); };
  }, [stop]);

  useEffect(() => {
    const safe = Math.max(0.5, Math.min(2.0, Number(playbackRate) || 1.0));
    if (audioRef.current) {
      try { audioRef.current.playbackRate = safe; } catch (_) { /* ignore */ }
    }
  }, [playbackRate]);

  const handlePause = useCallback(() => {
    if (usingSpeech) {
      stop();
      setPlaying(false);
    } else if (audioRef.current) {
      audioRef.current.pause();
    }
  }, [usingSpeech, stop]);

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !audioRef.current.muted;
      setMuted(!muted);
    }
  };

  if (!audioUrl && !fallbackText) return null;

  const overlayClass = `audio-overlay audio-overlay-${mode}${blocked ? ' audio-blocked' : ''}`;

  return (
    <div className={overlayClass}>
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          data-testid="event-audio"
          data-audio-url={audioUrl}
          onEnded={() => {
            setPlaying(false);
            _logAudioEvent({ kind: 'audio-ended', url: audioUrl, source: 'overlay' });
            if (onEndedRef.current) onEndedRef.current();
          }}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
        />
      )}
      <button className="audio-toggle" onClick={playing ? handlePause : play}
        title={blocked ? 'Click to play coach audio' : (playing ? 'Pause' : 'Play')}>
        {playing ? '\u23F8' : '\u25B6'}
      </button>
      <span className="audio-label">
        {blocked ? 'Click \u25B6 to hear coach' : (usingSpeech ? 'Coach Voice' : 'Coach Audio')}
      </span>
      {audioUrl && !blocked && (
        <button className="audio-mute" onClick={toggleMute}>
          {muted ? '\uD83D\uDD07' : '\uD83D\uDD0A'}
        </button>
      )}
    </div>
  );
}

AudioOverlay.kind = 'audio_overlay';
AudioOverlay.displayName = 'AudioOverlay';
